import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import {
  getDvSourceImageUrl,
  getLandingPageTitleColor,
  getThemeButtonGradient,
  getThemeButtonTextColor,
} from "../../functions/themeAssets";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  ensureStageRowId,
  fetchGameStageRecord,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveBinTossProgress,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import { getBinTossBins, getBinTossItems } from "../../functions/binToss";
import "./dragdrop.css";
import "./bintoss.css";

const STAGE_NUM = 1;
const DEMO_PLANS_URL = "https://extramileplay.com/plans";
const DEFAULT_IMG_COUNT = 5;
/** Delay before the next batch rides in on the belt, and how long that animation runs. */
const NEXT_BATCH_DELAY_MS = 800;
/** How long a batch rides in on the belt — must match --bt-belt-ride in bintoss.css. */
const CONVEYOR_ANIM_MS = 4200;
const WRONG_SHAKE_MS = 500;
/** How long a bin's correct/wrong reaction and its floating "+N" / "−N" stay on screen. */
const BIN_FX_MS = 700;
const FLOAT_MS = 1100;

function getStoredUser(authUser) {
  if (authUser && typeof authUser === "object") return authUser;
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}") || {};
  } catch {
    return {};
  }
}

function formatClock(totalSec) {
  const sec = Math.max(0, Math.floor(Number(totalSec) || 0));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

function shuffle(list) {
  const arr = [...list];
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Star rating + headline from accuracy (correct drops ÷ all drops). */
function rateRun(sorted, wrong) {
  const attempts = sorted + wrong;
  const accuracy = attempts ? Math.round((sorted / attempts) * 100) : 100;
  if (accuracy >= 90) return { accuracy, stars: 3, headline: "Outstanding!" };
  if (accuracy >= 70) return { accuracy, stars: 2, headline: "Great Job!" };
  return { accuracy, stars: 1, headline: "Well Done!" };
}

/** End-of-game summary (styled as .bt-done-* in bintoss.css). The header and button
 *  use the theme's button gradient. */
function showCompletedPopup({ points, time, wrong, sorted, themeData }) {
  const { start, end } = getThemeButtonGradient(themeData);
  const btnText = getThemeButtonTextColor(themeData);
  const { accuracy, stars, headline } = rateRun(sorted, wrong);

  const starIcons = [1, 2, 3]
    .map(
      (n) =>
        `<i class="fa-solid fa-star bt-done__star${n <= stars ? " is-on" : ""}" ` +
        `style="animation-delay:${0.25 + n * 0.15}s"></i>`
    )
    .join("");
  const stat = (mod, icon, value, label) =>
    `<div class="bt-done__stat bt-done__stat--${mod}">` +
    `<span class="bt-done__stat-label"><i class="fa-solid ${icon}" aria-hidden="true"></i>${label}</span>` +
    `<span class="bt-done__stat-value">${value}</span>` +
    `</div>`;

  return Swal.fire({
    html:
      '<div class="bt-done">' +
      '<div class="bt-done__hero">' +
      '<div class="bt-done__badge" aria-hidden="true"><i class="fa-solid fa-trophy"></i></div>' +
      `<h2 class="bt-done__title">${headline}</h2>` +
      `<p class="bt-done__subtitle">Game completed · all ${sorted} item${sorted === 1 ? "" : "s"} sorted</p>` +
      `<div class="bt-done__stars" role="img" aria-label="${stars} out of 3 stars">${starIcons}</div>` +
      "</div>" +
      '<div class="bt-done__body">' +
      '<div class="bt-done__score">' +
      `<span class="bt-done__score-value">${points}</span>` +
      '<span class="bt-done__score-label">Total points</span>' +
      "</div>" +
      '<div class="bt-done__stats">' +
      stat("time", "fa-clock", time, "Time") +
      stat("accuracy", "fa-bullseye", `${accuracy}%`, "Accuracy") +
      stat("wrong", "fa-xmark", wrong, "Wrong") +
      "</div>" +
      "</div>" +
      "</div>",
    confirmButtonText: 'Continue <i class="fa-solid fa-arrow-right" aria-hidden="true"></i>',
    buttonsStyling: false,
    allowOutsideClick: false,
    allowEscapeKey: false,
    heightAuto: false,
    scrollbarPadding: false,
    customClass: {
      container: "bt-done-backdrop",
      popup: "bt-done-popup",
      htmlContainer: "bt-done-html",
      actions: "bt-done-actions",
      confirmButton: "bt-done-btn",
    },
    showClass: { popup: "bt-done-popup--in" },
    didOpen: (popup) => {
      if (start) {
        popup.style.setProperty("--bt-start", start);
        popup.style.setProperty("--bt-end", end || start);
      }
      if (btnText) popup.style.setProperty("--bt-text", btnText);
    },
  });
}

export default function Game() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((s) => s.auth);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const storedUser = useMemo(() => getStoredUser(user), [user]);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);

  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const bins = useMemo(() => getBinTossBins(themeData), [themeData]);
  const rightPoints = Math.max(1, parseInt(themeData?.points, 10) || 1);
  const wrongPoints = Math.max(0, parseInt(themeData?.wrong_points, 10) || 0);
  const imgShowCount = Math.max(1, parseInt(themeData?.img_count, 10) || DEFAULT_IMG_COUNT);
  const titleColor = getLandingPageTitleColor(themeData) || "#1a1a1a";
  const heading = String(themeData?.landing_page_title || "").trim();
  const beltImage = themeData?.dice_image ? getDvSourceImageUrl(themeData.dice_image) : "";

  const [dbReady, setDbReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );
  const [batch, setBatch] = useState([]);
  /** Correctly sorted items shown faded inside each bin until the next batch arrives. */
  const [itemsInBins, setItemsInBins] = useState({});
  const [points, setPoints] = useState(0);
  const [wrongCount, setWrongCount] = useState(0);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [timerOn, setTimerOn] = useState(false);
  const [conveyorArriving, setConveyorArriving] = useState(false);
  const [wrongItemId, setWrongItemId] = useState(null);
  /** Item being dragged: { item, x, y, offX, offY, w, h } — drawn as a floating copy. */
  const [drag, setDrag] = useState(null);
  const [overBin, setOverBin] = useState(null);
  /** Items correctly sorted into each bin over the whole game (bin id → count). */
  const [binCounts, setBinCounts] = useState({});
  /** Bin reacting to the last drop: bin id → { kind: "correct" | "wrong", key }. */
  const [binFx, setBinFx] = useState({});
  /** Floating "+N" / "−N" labels: [{ id, binId, text, kind }]. */
  const [floats, setFloats] = useState([]);
  /** The drag hint shows until the player picks something up for the first time. */
  const [hasDragged, setHasDragged] = useState(false);
  /** Height of the logo + Back header when it sits above the page (phones); 0 when it
   *  floats over the page (desktop). The page fills the screen minus this, so no scroll. */
  const [headerHeight, setHeaderHeight] = useState(0);

  const startedRef = useRef(false);
  const finished = useRef(false);
  const totalRef = useRef(0);
  const sortedRef = useRef(0);
  const batchRef = useRef([]);
  const queueRef = useRef([]);
  const pointsRef = useRef(0);
  const wrongRef = useRef(0);
  const elapsedRef = useRef(0);
  const answersRef = useRef([]);
  const dragRef = useRef(null);
  const binRefs = useRef(new Map());
  const timeoutsRef = useRef([]);

  const later = useCallback((fn, ms) => {
    timeoutsRef.current.push(window.setTimeout(fn, ms));
  }, []);
  useEffect(() => () => timeoutsRef.current.forEach((t) => window.clearTimeout(t)), []);

  useEffect(() => {
    dispatch(setBackButtonUrl("/rules"));
  }, [dispatch]);

  // Unlock document scroll for the stage (login/rules CSS locks body overflow on mobile portrait).
  useEffect(() => {
    const body = document.body;
    const html = document.documentElement;
    const root = document.getElementById("root");
    body.classList.add("dd-stage-open");
    html.classList.add("dd-stage-open");

    const apply = () => {
      html.style.setProperty("overflow-x", "hidden", "important");
      html.style.setProperty("overflow-y", "auto", "important");
      html.style.setProperty("height", "auto", "important");
      html.style.setProperty("max-height", "none", "important");
      body.style.setProperty("overflow", "visible", "important");
      body.style.setProperty("height", "auto", "important");
      body.style.setProperty("max-height", "none", "important");
      if (root) {
        root.style.setProperty("overflow", "visible", "important");
        root.style.setProperty("height", "auto", "important");
      }
    };

    apply();
    window.addEventListener("resize", apply);
    window.addEventListener("orientationchange", apply);

    return () => {
      window.removeEventListener("resize", apply);
      window.removeEventListener("orientationchange", apply);
      body.classList.remove("dd-stage-open");
      html.classList.remove("dd-stage-open");
      ["overflow", "overflow-x", "overflow-y", "height", "max-height"].forEach((prop) => {
        html.style.removeProperty(prop);
        body.style.removeProperty(prop);
        root?.style.removeProperty(prop);
      });
    };
  }, []);

  useLayoutEffect(() => {
    const measure = () => {
      const header = document.querySelector("header.upperaction");
      if (!header) return setHeaderHeight(0);
      const { position } = window.getComputedStyle(header);
      const floats = position === "absolute" || position === "fixed";
      setHeaderHeight(floats ? 0 : Math.ceil(header.getBoundingClientRect().height));
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    // Coming from /rules, its cleanup (removing html.sl-rules-open, which forces the header
    // into flow) runs after this effect — re-measure when those classes change.
    const observer = new MutationObserver(measure);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    const header = document.querySelector("header.upperaction");
    if (header) observer.observe(header, { attributes: true, attributeFilter: ["class"] });
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
      observer.disconnect();
    };
  }, []);

  // Already played → straight to the result page.
  useEffect(() => {
    let cancelled = false;
    async function bootstrap() {
      setStageActive(STAGE_NUM, storedUser);
      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }
      try {
        const rowId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          onStageRowId: (id) => {
            sessionStorage.setItem(STAGE_ROW_KEY, String(id));
            if (!cancelled) setStageRowId(String(id));
          },
        });
        const st = await fetchGameStageRecord({
          backendBase,
          adminToken,
          storedUser,
          stageId: rowId || sessionStorage.getItem(STAGE_ROW_KEY),
        });
        if (st && typeof st === "object") {
          hydrateCompletedStagesFromRecord(st);
          const redirect = getRedirectForPlayedStage(st, STAGE_NUM, {});
          if (redirect && redirect !== "/stage1" && redirect !== "/game") {
            if (!cancelled) navigate(redirect, { replace: true });
            return;
          }
        }
      } catch (err) {
        console.error("Game bootstrap:", err);
      } finally {
        if (!cancelled) setDbReady(true);
      }
    }
    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [adminToken, backendBase, isDemoBypass, navigate, storedUser]);

  // Shuffle every item once, put the first `img_count` on the belt and start the clock.
  useEffect(() => {
    if (startedRef.current || !dbReady || !themeData) return;
    startedRef.current = true;

    const items = shuffle(getBinTossItems(themeData));
    if (bins.length < 2 || !items.length) {
      Swal.fire({
        icon: "warning",
        title: "No Images Found",
        text: "Please add bins and item images from Admin before continuing.",
        confirmButtonText: "OK",
      }).then(() => navigate("/rules", { replace: true }));
      return;
    }

    totalRef.current = items.length;
    batchRef.current = items.slice(0, imgShowCount);
    queueRef.current = items.slice(imgShowCount);
    setBatch(batchRef.current);
    setConveyorArriving(true);
    later(() => setConveyorArriving(false), CONVEYOR_ANIM_MS);
    setTimerOn(true);
  }, [dbReady, themeData, bins.length, imgShowCount, later, navigate]);

  useEffect(() => {
    if (!timerOn) return undefined;
    const id = setInterval(() => {
      elapsedRef.current += 1;
      setElapsedSec(elapsedRef.current);
    }, 1000);
    return () => clearInterval(id);
  }, [timerOn]);

  const finishGame = useCallback(async () => {
    const finalScore = pointsRef.current;
    const finalTime = formatClock(elapsedRef.current);
    setTimerOn(false);
    try {
      if (!isDemoBypass && backendBase && adminToken) {
        const saveResult = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
          stageNumber: STAGE_NUM,
          score: finalScore,
          total_score: finalScore,
          time: finalTime,
          gameover: true,
          wrongcount: wrongRef.current,
          currentQues: sortedRef.current,
          user_answers: answersRef.current,
        });
        if (saveResult?.stageId) {
          sessionStorage.setItem(STAGE_ROW_KEY, String(saveResult.stageId));
          setStageRowId(String(saveResult.stageId));
        }
      }
    } catch (err) {
      console.error("Game finish:", err);
    }
    clearStageActive(STAGE_NUM);
    markStageCompleted(STAGE_NUM, storedUser);

    await showCompletedPopup({
      points: finalScore,
      time: finalTime,
      wrong: wrongRef.current,
      sorted: sortedRef.current,
      themeData,
    });
    navigate("/thankyou", {
      replace: true,
      state: { points: finalScore, time: finalTime },
    });
  }, [adminToken, backendBase, isDemoBypass, navigate, stageRowId, storedUser, themeData]);

  /** Live score / wrong count / items sorted on the stages row after every drop. */
  const saveProgress = useCallback(() => {
    saveBinTossProgress({
      backendBase,
      adminToken,
      storedUser,
      isDemoBypass,
      stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
      totalScore: pointsRef.current,
      wrongcount: wrongRef.current,
      currentQues: sortedRef.current,
      time: formatClock(elapsedRef.current),
    });
  }, [adminToken, backendBase, isDemoBypass, stageRowId, storedUser]);

  /** Bin bounce / shake plus a floating score label for one drop. */
  const showDropFeedback = useCallback((binId, kind, text) => {
    const key = `${Date.now()}-${Math.random()}`;
    setBinFx((prev) => ({ ...prev, [binId]: { kind, key } }));
    later(
      () =>
        setBinFx((prev) => {
          if (prev[binId]?.key !== key) return prev;
          const next = { ...prev };
          delete next[binId];
          return next;
        }),
      BIN_FX_MS
    );
    setFloats((prev) => [...prev, { id: key, binId, text, kind }]);
    later(() => setFloats((prev) => prev.filter((f) => f.id !== key)), FLOAT_MS);
  }, [later]);

  const handleDrop = useCallback(
    (binId, item) => {
      if (finished.current) return;
      const bin = bins.find((b) => b.id === binId);
      const isCorrect = binId === item.bin;
      answersRef.current.push({
        questionText: item.url.split("/").pop(),
        userAnswer: bin?.name || "",
        correctAnswer: bins.find((b) => b.id === item.bin)?.name || "",
        isCorrect,
        questionNumber: answersRef.current.length + 1,
        answeredAt: new Date().toISOString(),
      });

      if (!isCorrect) {
        wrongRef.current += 1;
        pointsRef.current -= wrongPoints;
        setWrongCount(wrongRef.current);
        setPoints(pointsRef.current);
        setWrongItemId(item.id);
        later(() => setWrongItemId(null), WRONG_SHAKE_MS);
        showDropFeedback(binId, "wrong", wrongPoints > 0 ? `−${wrongPoints}` : "✕");
        saveProgress();
        return;
      }

      pointsRef.current += rightPoints;
      sortedRef.current += 1;
      setPoints(pointsRef.current);
      setBinCounts((prev) => ({ ...prev, [binId]: (prev[binId] || 0) + 1 }));
      showDropFeedback(binId, "correct", `+${rightPoints}`);
      setItemsInBins((prev) => ({ ...prev, [binId]: [...(prev[binId] || []), item] }));
      batchRef.current = batchRef.current.filter((i) => i.id !== item.id);
      setBatch(batchRef.current);

      // Demo sessions only get a taste of one sorted item, then a CTA to subscribe.
      if (isDemoBypass) {
        finished.current = true;
        setTimerOn(false);
        later(() => {
          Swal.fire({
            icon: "info",
            text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
            confirmButtonText: "OK",
            allowOutsideClick: false,
            allowEscapeKey: false,
          }).then(() => window.location.assign(DEMO_PLANS_URL));
        }, 500);
        return;
      }

      if (sortedRef.current >= totalRef.current) {
        finished.current = true;
        setTimerOn(false);
        later(finishGame, 500);
        return;
      }
      saveProgress();

      // Belt is empty → the next batch rides in from the right.
      if (!batchRef.current.length && queueRef.current.length) {
        const next = queueRef.current.slice(0, imgShowCount);
        queueRef.current = queueRef.current.slice(imgShowCount);
        later(() => {
          setItemsInBins({});
          batchRef.current = next;
          setBatch(next);
          setConveyorArriving(true);
        }, NEXT_BATCH_DELAY_MS);
        later(() => setConveyorArriving(false), NEXT_BATCH_DELAY_MS + CONVEYOR_ANIM_MS);
      }
    },
    [
      bins,
      finishGame,
      imgShowCount,
      isDemoBypass,
      later,
      rightPoints,
      saveProgress,
      showDropFeedback,
      wrongPoints,
    ]
  );

  /* ---------------- Pointer drag (mouse + touch) ---------------- */

  const binAt = (x, y) => {
    for (const [id, el] of binRefs.current) {
      const r = el.getBoundingClientRect();
      if (x >= r.left && x <= r.right && y >= r.top && y <= r.bottom) return id;
    }
    return null;
  };

  const onItemPointerDown = (e, item) => {
    if (finished.current || dragRef.current || conveyorArriving) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const next = {
      item,
      pointerId: e.pointerId,
      x: e.clientX,
      y: e.clientY,
      offX: e.clientX - rect.left,
      offY: e.clientY - rect.top,
      w: rect.width,
      h: rect.height,
    };
    dragRef.current = next;
    setDrag(next);
    setHasDragged(true);
    try {
      e.currentTarget.setPointerCapture(e.pointerId);
    } catch {
      /* capture unsupported */
    }
  };

  const onItemPointerMove = (e) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const next = { ...d, x: e.clientX, y: e.clientY };
    dragRef.current = next;
    setDrag(next);
    setOverBin(binAt(e.clientX, e.clientY));
  };

  const endDrag = (e, dropIt) => {
    const d = dragRef.current;
    if (!d || d.pointerId !== e.pointerId) return;
    dragRef.current = null;
    setDrag(null);
    setOverBin(null);
    const binId = dropIt ? binAt(e.clientX, e.clientY) : null;
    if (binId != null) handleDrop(binId, d.item);
  };

  const themeVars = useMemo(() => {
    const vars = {};
    const { start, end } = getThemeButtonGradient(themeData);
    const text = getThemeButtonTextColor(themeData);
    if (start) {
      vars["--theme-button-start"] = start;
      vars["--theme-button-end"] = end || start;
    }
    if (text) vars["--theme-button-text"] = text;
    vars["--bt-header-h"] = `${headerHeight}px`;
    return vars;
  }, [themeData, headerHeight]);

  if ((themeStatus === "loading" && !themeData) || !dbReady) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    // No background here: the theme background is set on <body> (see dddUI/user.jsx).
    <div className={`dd-page bt-page${drag ? " is-dragging" : ""}`} style={themeVars}>
      <div className="bt-hud">
        <div className="dd-hud-pill dd-hud-pill--timer bt-hud-pill" aria-label={`Time ${formatClock(elapsedSec)}`}>
          <span className="bt-hud-badge bt-hud-badge--time" aria-hidden>
            <i className="fa-solid fa-stopwatch" />
          </span>
          <span className="bt-hud-label">Time:</span>
          <span className="bt-hud-value bt-hud-value--static">{formatClock(elapsedSec)}</span>
        </div>

        <div className="bt-hud-group">
          <div className="dd-hud-pill dd-hud-pill--points bt-hud-pill" aria-label={`Points ${points}`}>
            <span className="bt-hud-badge bt-hud-badge--points" aria-hidden>
              <i className="fa-solid fa-star" />
            </span>
            <span className="bt-hud-label">Points:</span>
            <span key={points} className="bt-hud-value">
              {points}
            </span>
          </div>
          <div className="dd-hud-pill dd-hud-pill--points bt-hud-pill" aria-label={`Wrong count ${wrongCount}`}>
            <span className="bt-hud-badge bt-hud-badge--wrong" aria-hidden>
              <i className="fa-solid fa-xmark" />
            </span>
            <span className="bt-hud-label">Wrong count:</span>
            <span key={`w${wrongCount}`} className="bt-hud-value">
              {wrongCount}
            </span>
          </div>
        </div>
      </div>

      {heading ? (
        <h3 className="bt-title" style={{ color: titleColor }}>
          {heading}
        </h3>
      ) : null}

      <div className="bt-bins">
        {bins.map((bin) => {
          const fx = binFx[bin.id];
          const count = binCounts[bin.id] || 0;
          return (
            <div
              key={bin.id}
              ref={(el) => {
                if (el) binRefs.current.set(bin.id, el);
                else binRefs.current.delete(bin.id);
              }}
              className={`bt-bin${overBin === bin.id ? " is-over" : ""}${
                fx ? ` is-${fx.kind}` : ""
              }`}
              aria-label={`${bin.name}: ${count} sorted`}
            >
              <span className="bt-bin-drop-chip" aria-hidden>
                Drop here
              </span>
              <span key={`c${count}`} className={`bt-bin-count${count ? "" : " is-empty"}`}>
                {count}
              </span>
              <div className="bt-bin-art">
                {bin.icon ? (
                  <img className="bt-bin-icon" src={bin.icon} alt={bin.name} draggable={false} />
                ) : (
                  <span className="bt-bin-fallback" style={{ color: titleColor }}>
                    {bin.name}
                  </span>
                )}
                <div className="bt-bin-items" aria-hidden>
                  {(itemsInBins[bin.id] || []).map((dropped) => (
                    <img key={dropped.id} src={dropped.url} alt="" draggable={false} />
                  ))}
                </div>
              </div>
              {floats
                .filter((f) => f.binId === bin.id)
                .map((f) => (
                  <span key={f.id} className={`bt-float bt-float--${f.kind}`} aria-hidden>
                    {f.text}
                  </span>
                ))}
            </div>
          );
        })}
      </div>

      <div
        className={`bt-belt${beltImage ? "" : " bt-belt--plain"}${conveyorArriving ? " is-moving" : ""}`}
        style={{
          backgroundColor: themeData?.belt_color ? String(themeData.belt_color).slice(0, 7) : undefined,
          backgroundImage: beltImage ? `url("${beltImage}")` : undefined,
        }}
      >
        {!hasDragged && batch.length && !conveyorArriving ? (
          <div className="bt-hint" aria-hidden>
            <i className="fa-solid fa-hand-pointer" /> Drag each item into the correct bin
          </div>
        ) : null}
        <div className={`bt-belt-items${conveyorArriving ? " is-arriving" : ""}`}>
          {batch.map((item) => (
            <div
              key={item.id}
              className={`bt-item${drag?.item.id === item.id ? " is-dragging" : ""}${
                wrongItemId === item.id ? " is-wrong" : ""
              }`}
              onPointerDown={(e) => onItemPointerDown(e, item)}
              onPointerMove={onItemPointerMove}
              onPointerUp={(e) => endDrag(e, true)}
              onPointerCancel={(e) => endDrag(e, false)}
            >
              <img src={item.url} alt="" draggable={false} />
            </div>
          ))}
        </div>
      </div>

      {drag ? (
        <div
          className={`bt-drag-ghost${overBin != null ? " is-over-bin" : ""}`}
          style={{
            left: drag.x - drag.offX,
            top: drag.y - drag.offY,
            width: drag.w,
            height: drag.h,
          }}
          aria-hidden
        >
          <img src={drag.item.url} alt="" draggable={false} />
        </div>
      ) : null}
    </div>
  );
}
